import { useState } from 'react';
import Topbar from '../components/Topbar';
import { createAdmin, getAdmins, updateAdminStatus, deleteAdmin, updateAdmin, getSingleAdmin, forcePasswordResetAdmin, getSingleHistoryAdmin, getLoginHistoryAdmin, getAppSettings, updateAppSettings } from '../services/settingsService';
import { toast } from 'react-toastify';
import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useRefresh } from '../utils/RefreshContext';
import Spinner from '../components/Spinner';

const adminActions = [
  { label: 'Add new admin users', sub: 'Super Admin only — regular admins cannot create new accounts', iconBg: '#EAFAF3', iconColor: '#1A7A42', icon: <><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></> },
  { label: 'Deactivate or remove admin access', sub: 'Deactivated admins cannot log in — account is preserved but access is revoked', iconBg: '#FFF0F0', iconColor: 'var(--red)', icon: <><circle cx="12" cy="12" r="10" /><line x1="4.93" y1="4.93" x2="19.07" y2="19.07" /></> },
  { label: 'View admin activity history and IP addresses', sub: 'Full log of every system action — timestamp, IP address, and details', iconBg: '#EEF6FF', iconColor: 'var(--blue)', icon: <><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /></> },
  { label: 'Force password reset for any admin account', sub: 'Sends password reset email immediately — admin must reset before next login', iconBg: '#FFF4E0', iconColor: '#B8860B', icon: <><rect x="3" y="11" width="18" height="11" rx="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" /></> },
];

const Settings = () => {

  const { refreshKey } = useRefresh();
  const [searchParams] = useSearchParams();
  const tab = searchParams.get('tab');
  const [activeTab, setActiveTab] = useState(tab ? tab : 'Admin Accounts');
  const [showAddAdmin, setShowAddAdmin] = useState(false);
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [adminToDelete, setAdminToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [showAdminView, setShowAdminView] = useState(false);
  const [selectedAdminDetails, setSelectedAdminDetails] = useState(null);
  const [adminHistory, setAdminHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [globalLoginHistory, setGlobalLoginHistory] = useState([]);
  const [currentAdminId, setCurrentAdminId] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);

  // Pagination & Filters for Admin Accounts
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const limit = 10;

  // Pagination for Login History
  const [historyPage, setHistoryPage] = useState(1);
  const [historyTotalPages, setHistoryTotalPages] = useState(1);
  const [historyTotalRecords, setHistoryTotalRecords] = useState(0);

  // Pagination for Individual Admin History
  const [indivHistoryPage, setIndivHistoryPage] = useState(1);
  const [indivHistoryTotalPages, setIndivHistoryTotalPages] = useState(1);
  const [indivHistoryTotalRecords, setIndivHistoryTotalRecords] = useState(0);

  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    password: '',
    role: 'Super Admin',
    status: 1
  });

  const [appSettings, setAppSettings] = useState({
    map_link: '',
    app_version: '',
    build_number: '',
    force_update: false,
    is_mock_location_enabled: false
  });
  const [savingSettings, setSavingSettings] = useState(false);
  const [settingsLoaded, setSettingsLoaded] = useState(false);

  const fetchAdmins = async () => {
    setLoading(true);
    try {
      const response = await getAdmins({
        page: currentPage,
        limit,
        search,
        role: roleFilter,
        status: statusFilter
      });
      if (response && response.result) {
        setAdmins(response.data);
        setTotalPages(response.meta?.totalPages || 1);
        setTotalRecords(response.meta?.total || 0);
      }
    } catch (error) {
      console.error('Error fetching admins:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAdmins();
  }, [currentPage, roleFilter, statusFilter, refreshKey]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (currentPage !== 1) {
        setCurrentPage(1);
      } else {
        fetchAdmins();
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    fetchGlobalHistory();
  }, [historyPage, refreshKey]);

  useEffect(() => {
    if (showAdminView && selectedAdminDetails && adminHistory.length > 0) {
      fetchIndividualHistory(selectedAdminDetails.id, indivHistoryPage);
    }
  }, [indivHistoryPage, refreshKey]);

  useEffect(() => {
    const userStr = localStorage.getItem('adminUser');
    if (userStr) {
      setCurrentUser(JSON.parse(userStr));
    }
  }, []);

  const fetchAppSettings = async () => {
    try {
      const response = await getAppSettings();
      if (response && response.result && response.data) {
        setAppSettings({
          map_link: response.data.map_link || '',
          app_version: response.data.app_version || '',
          build_number: response.data.build_number || '',
          force_update: response.data.force_update === true || response.data.force_update === 'true',
          is_mock_location_enabled: response.data.is_mock_location_enabled === true || response.data.is_mock_location_enabled === 'true'
        });
        setSettingsLoaded(true);
      }
    } catch (error) {
      console.error('Error fetching app settings:', error);
    }
  };

  useEffect(() => {
    if (activeTab === 'App Settings' && !settingsLoaded) {
      fetchAppSettings();
    }
  }, [activeTab]);

  const handleAppSettingsChange = (e) => {
    const { name, value, type, checked } = e.target;
    setAppSettings(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleAppSettingsToggle = (field) => {
    setAppSettings(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  const handleSaveAppSettings = async () => {
    setSavingSettings(true);
    try {
      const response = await updateAppSettings({
        map_link: appSettings.map_link,
        app_version: appSettings.app_version,
        build_number: appSettings.build_number,
        force_update: appSettings.force_update.toString(),
        is_mock_location_enabled: appSettings.is_mock_location_enabled.toString()
      });
      if (response && response.result) {
        toast.success(response.message || 'Settings updated successfully');
      } else {
        toast.error('Failed to update settings');
      }
    } catch (error) {
      console.error(error);
      toast.error('Failed to update settings');
    } finally {
      setSavingSettings(false);
    }
  };

  const fetchGlobalHistory = async () => {
    setLoadingHistory(true);
    try {
      const response = await getLoginHistoryAdmin({
        page: historyPage,
        limit
      });
      if (response && response.result) {
        setGlobalLoginHistory(response.data);
        setHistoryTotalPages(response.meta?.totalPages || 1);
        setHistoryTotalRecords(response.meta?.total || 0);
      }
    } catch (error) {
      console.error('Error fetching global history:', error);
    } finally {
      setLoadingHistory(false);
    }
  };

  const fetchIndividualHistory = async (adminId, page = 1) => {
    setLoadingHistory(true);
    try {
      const res = await getSingleHistoryAdmin(adminId, { page, limit });
      if (res && res.result) {
        setAdminHistory(res.data);
        setIndivHistoryTotalPages(res.meta?.totalPages || 1);
        setIndivHistoryTotalRecords(res.meta?.total || 0);
      }
    } catch (error) {
      console.error(error);
      toast.error('Failed to fetch activity history');
    } finally {
      setLoadingHistory(false);
    }
  };

  const canManage = (targetAdmin) => {
    if (!currentUser || !targetAdmin) return false;

    // Normalize role from current user (handle string or object as per localStorage)
    const currentRoleRaw = typeof currentUser.role === 'string' ? currentUser.role : currentUser.role?.name;
    const currentRole = currentRoleRaw?.toLowerCase();

    const isTargetSelf = currentUser.id === targetAdmin.id;
    const isCurrentSuper = currentRole === 'superadmin';

    // 1. Super Admin can manage anyone except themselves
    if (isCurrentSuper) return !isTargetSelf;

    // 2. Regular Admin logic (Read-only access)
    // Standard admins cannot perform any management actions (delete, update, status, reset)
    return false;
  };

  const getInitials = (name) => {
    if (!name) return '??';
    const parts = name.split(' ');
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
    return parts[0].substring(0, 2).toUpperCase();
  };

  const handleToggleStatus = async (id, currentStatus) => {
    const newStatus = currentStatus === 1 ? 2 : 1;
    try {
      const response = await updateAdminStatus(id, { status: newStatus });
      if (response && (response.status === 200 || response.status === 201)) {
        toast.success(response?.data?.message || `Admin ${newStatus === 1 ? 'activated' : 'deactivated'} successfully`);
        fetchAdmins();
      }
    } catch (error) {
      console.error('Error toggling status:', error);
      toast.error('Failed to update admin status');
    }
  };

  const confirmDelete = (admin) => {
    setAdminToDelete(admin);
    setShowDeleteModal(true);
  };

  const handleDelete = async () => {
    if (!adminToDelete) return;
    setIsDeleting(true);
    try {
      const response = await deleteAdmin(adminToDelete.id);
      if (response && response.result) {
        toast.success(response.message || 'Admin deleted successfully');
        fetchAdmins();
        setShowDeleteModal(false);
        setAdminToDelete(null);
      }
    } catch (error) {
      console.error('Error deleting admin:', error);
      toast.error('Failed to delete admin');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleView = async (adminId) => {
    setLoading(true);
    try {
      const response = await getSingleAdmin(adminId);
      if (response && response.result) {
        setSelectedAdminDetails(response.data);
        setShowAdminView(true);
      }
    } catch (error) {
      console.error('Error fetching admin details:', error);
      toast.error('Failed to fetch admin details');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (admin) => {
    setIsEditing(true);
    setCurrentAdminId(admin.id);
    setFormData({
      full_name: admin.full_name,
      email: admin.email,
      password: '', // Keep empty unless changing
      role: admin.role?.name?.toLowerCase() === 'superadmin' ? 'Super Admin' : 'Admin',
      status: admin.status
    });
    setShowAddAdmin(true);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleRoleChange = (role) => {
    setFormData(prev => ({ ...prev, role }));
  };

  const handleStatusToggle = () => {
    setFormData(prev => ({ ...prev, status: prev.status === 1 ? 2 : 1 }));
  };

  const handleActionClick = async (label) => {
    if (!selectedAdminDetails && label !== 'Add new admin users') return;

    switch (label) {
      case 'Add new admin users':
        setShowAddAdmin(true);
        setShowAdminView(false);
        setIsEditing(false);
        setFormData({ full_name: '', email: '', password: '', role: 'Super Admin', status: 1 });
        break;
      case 'Deactivate or remove admin access':
        await handleToggleStatus(selectedAdminDetails.id, selectedAdminDetails.status);
        // Refresh details
        const updateRes = await getSingleAdmin(selectedAdminDetails.id);
        if (updateRes && updateRes.result) setSelectedAdminDetails(updateRes.data);
        break;
      case 'Force password reset for any admin account':
        await handleForcePasswordReset(selectedAdminDetails.id);
        break;
      case 'View admin activity history and IP addresses':
        setIndivHistoryPage(1);
        await fetchIndividualHistory(selectedAdminDetails.id, 1);
        break;
      default:
        break;
    }
  };

  const renderPagination = (current, total, setPage) => {
    const pages = [];
    if (total <= 7) {
      for (let i = 1; i <= total; i++) pages.push(i);
    } else {
      if (current <= 4) {
        for (let i = 1; i <= 5; i++) pages.push(i);
        pages.push('...');
        pages.push(total);
      } else if (current >= total - 3) {
        pages.push(1);
        pages.push('...');
        for (let i = total - 4; i <= total; i++) pages.push(i);
      } else {
        pages.push(1);
        pages.push('...');
        pages.push(current - 1);
        pages.push(current);
        pages.push(current + 1);
        pages.push('...');
        pages.push(total);
      }
    }
    return pages;
  };

  const handleForcePasswordReset = async (adminId) => {
    try {
      const res = await forcePasswordResetAdmin(adminId);
      if (res && (res.status === 200 || res.status === 201)) {
        toast.success(res.data?.message || 'Password reset email sent successfully');
      }
    } catch (error) {
      console.error(error);
      toast.error('Failed to force password reset');
    }
  };

  const handleSubmit = async () => {
    try {
      // Map role to backend expected format (remove space for SuperAdmin)
      const payload = {
        ...formData,
        role: formData.role === 'Super Admin' ? 'SuperAdmin' : formData.role
      };

      let response;
      if (isEditing) {
        // Remove password from payload if it's empty
        if (!payload.password) delete payload.password;
        response = await updateAdmin(currentAdminId, payload);
      } else {
        response = await createAdmin(payload);
      }

      if (response && (response.status === 200 || response.status === 201)) {
        toast.success(response?.data?.message || `Admin account ${isEditing ? 'updated' : 'created'} successfully`);
        setShowAddAdmin(false);
        setIsEditing(false);
        setCurrentAdminId(null);
        fetchAdmins();
        setFormData({
          full_name: '',
          email: '',
          password: '',
          role: 'Super Admin',
          status: 1
        });
      }
    } catch (error) {
      console.error(error);
    }
  };

  if (loading || loadingHistory) return <Spinner />;

  return (
    <div>
      <Topbar title="Settings & Configuration" subtitle="Admin account management and system configuration" />
      <div className="content">
        <div className="screen-label">6.1 — Admin Account Management</div>
        <div className="card">
          <div className="settings-layout">

            {/* LEFT TABS */}
            <div className="settings-tabs">
              <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--grey)', textTransform: 'uppercase', letterSpacing: 1, padding: '12px 20px 6px' }}>Settings</div>
              <div className={`tab-item ${activeTab === 'Admin Accounts' ? 'active' : ''}`} onClick={() => { setActiveTab('Admin Accounts'); setShowAddAdmin(false); setIsEditing(false); }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={activeTab === 'Admin Accounts' ? 'var(--blue)' : 'var(--grey)'} strokeWidth="1.8"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" /></svg>
                <span>Admin Accounts</span>
              </div>
              <div className={`tab-item ${activeTab === 'Login History' ? 'active' : ''}`} onClick={() => { setActiveTab('Login History'); setShowAddAdmin(false); setIsEditing(false); }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={activeTab === 'Login History' ? 'var(--blue)' : 'var(--grey)'} strokeWidth="1.8"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /><line x1="12" y1="18" x2="12" y2="12" /><line x1="9" y1="15" x2="15" y2="15" /></svg>
                <span>Login History</span>
              </div>
              <div className={`tab-item ${activeTab === 'App Settings' ? 'active' : ''}`} onClick={() => { setActiveTab('App Settings'); setShowAddAdmin(false); setIsEditing(false); }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={activeTab === 'App Settings' ? 'var(--blue)' : 'var(--grey)'} strokeWidth="1.8"><circle cx="12" cy="12" r="3" /><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" /></svg>
                <span>App Settings</span>
              </div>
            </div>

            {/* RIGHT CONTENT */}
            <div className="settings-content">

              {/* SUPER ADMIN ONLY NOTICE */}
              {activeTab === 'Admin Accounts' && (
                <div className="super-only">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#B8860B" strokeWidth="2"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /></svg>
                  <span>Admin account management is visible to Super Admin only. Regular admins cannot access this section.</span>
                </div>
              )}

              {activeTab === 'Admin Accounts' && !showAddAdmin && (
                <>
                  {showAdminView && selectedAdminDetails ? (
                    <>
                      {/* ADMIN VIEW HEADER */}
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
                        <div className="sec-title" style={{ marginBottom: 0 }}>Admin Profile: {selectedAdminDetails.full_name}</div>
                        <button className="btn btn-outline" onClick={() => setShowAdminView(false)} style={{ fontSize: 12, padding: '8px 14px' }}>
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M19 12H5M12 19l-7-7 7-7" /></svg>
                          Back to List
                        </button>
                      </div>

                      <div className="card" style={{ padding: 24, marginBottom: 24, background: 'var(--bg-light)', border: '1px solid var(--border)', borderRadius: 12 }}>
                        <div className="admin-view-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '24px' }}>
                          <div className="view-item">
                            <div style={{ fontSize: 11, color: 'var(--grey)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 4 }}>Full Name</div>
                            <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--navy)' }}>{selectedAdminDetails.full_name}</div>
                          </div>
                          <div className="view-item">
                            <div style={{ fontSize: 11, color: 'var(--grey)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 4 }}>Email Address</div>
                            <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--navy)' }}>{selectedAdminDetails.email}</div>
                          </div>
                          <div className="view-item">
                            <div style={{ fontSize: 11, color: 'var(--grey)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 4 }}>Phone Number</div>
                            <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--navy)' }}>{selectedAdminDetails.phone_number || 'Not provided'}</div>
                          </div>
                          <div className="view-item">
                            <div style={{ fontSize: 11, color: 'var(--grey)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 4 }}>Admin Role</div>
                            <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--navy)' }}><span className={`badge badge-${selectedAdminDetails.role?.name?.toLowerCase() === 'superadmin' ? 'super' : 'admin'}`}>{selectedAdminDetails.role?.name || 'N/A'}</span></div>
                          </div>
                          <div className="view-item">
                            <div style={{ fontSize: 11, color: 'var(--grey)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 4 }}>Status</div>
                            <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--navy)' }}><span className={`badge badge-${selectedAdminDetails.status === 1 ? 'active' : 'inactive'}`}>{selectedAdminDetails.status === 1 ? 'Active' : 'Inactive'}</span></div>
                          </div>
                          <div className="view-item">
                            <div style={{ fontSize: 11, color: 'var(--grey)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 4 }}>Account Created</div>
                            <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--navy)' }}>{selectedAdminDetails.createdAt ? new Date(selectedAdminDetails.createdAt).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) : 'N/A'}</div>
                          </div>
                          <div className="view-item">
                            <div style={{ fontSize: 11, color: 'var(--grey)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 4 }}>Email Verified</div>
                            <div style={{ fontSize: 14, fontWeight: 600, color: selectedAdminDetails.is_email_verified ? 'var(--green)' : 'var(--red)' }}>{selectedAdminDetails.is_email_verified ? 'Verified ✓' : 'Unverified ✗'}</div>
                          </div>
                        </div>
                      </div>

                      {/* ACTIONS INFO - NOW EXCLUSIVELY IN VIEW MODE */}
                      <div className="actions-info" style={{ marginTop: 0, padding: 24, border: '1px solid var(--border)', borderRadius: 12 }}>
                        <div className="actions-info-title">Admin Account Actions</div>
                        {adminActions.map((a, i) => {
                          // Security Check: Hide management actions based on permissions
                          const isManagementAction = a.label === 'Deactivate or remove admin access' || a.label === 'Force password reset for any admin account';
                          if (isManagementAction && !canManage(selectedAdminDetails)) {
                            return null;
                          }

                          // Hide "Add new" if user is not Super Admin
                          const currentRoleStr = (typeof currentUser?.role === 'string' ? currentUser.role : currentUser?.role?.name)?.toLowerCase();
                          if (a.label === 'Add new admin users' && currentRoleStr !== 'superadmin') {
                            return null;
                          }

                          let displayLabel = a.label;
                          let displaySub = a.sub;
                          let displayIcon = a.icon;
                          let displayIconBg = a.iconBg;
                          let displayIconColor = a.iconColor;

                          // Dynamic toggle for Activate/Deactivate
                          if (a.label === 'Deactivate or remove admin access' && selectedAdminDetails.status === 2) {
                            displayLabel = 'Activate admin account';
                            displaySub = 'Account is currently inactive — click to restore access immediately';
                            displayIcon = <><polyline points="20 6 9 17 4 12" /></>;
                            displayIconBg = '#EAFAF3';
                            displayIconColor = 'var(--green)';
                          }

                          return (
                            <div
                              key={i}
                              className="action-row interactive"
                              onClick={() => handleActionClick(a.label)}
                              style={{ cursor: 'pointer', transition: 'all 0.2s ease' }}
                            >
                              <div className="action-icon" style={{ background: displayIconBg }}>
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={displayIconColor} strokeWidth="2" strokeLinecap="round">{displayIcon}</svg>
                              </div>
                              <div>
                                <div style={{ fontSize: 13, color: 'var(--dark)', fontWeight: 500 }}>{displayLabel}</div>
                                <div style={{ fontSize: 11, color: 'var(--grey)', marginTop: 2 }}>{displaySub}</div>
                              </div>
                            </div>
                          );
                        })}
                      </div>

                      {/* INDIVIDUAL ADMIN HISTORY */}
                      {adminHistory.length > 0 && (
                        <div style={{ marginTop: 24 }}>
                          <div className="sec-title" style={{ fontSize: 15 }}>Recent Activity History: {selectedAdminDetails.full_name}</div>
                          <div style={{ position: 'relative' }}>
                            {loadingHistory && <div style={{ position: 'absolute', inset: 0, background: 'rgba(255,255,255,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10 }}>Loading history...</div>}
                            <table className="log-table" style={{ marginBottom: 0 }}>
                              <thead><tr><th>Action</th><th>Date & Time</th><th>IP Address</th><th>Result</th></tr></thead>
                              <tbody>
                                {adminHistory.map((l, i) => (
                                  <tr key={i}>
                                    <td style={{ fontSize: 13, color: 'var(--dark)', fontWeight: 500 }}>{l.action}</td>
                                    <td style={{ fontSize: 12, color: 'var(--grey)' }}>{new Date(l.createdAt).toLocaleString()}</td>
                                    <td style={{ fontSize: 12, color: 'var(--grey)', fontFamily: "'Space Grotesk', sans-serif" }}>{l.ip_address || '—'}</td>
                                    <td><span className={l.result?.toLowerCase().includes('success') ? 'log-success' : 'log-fail'}>{l.result}</span></td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>

                            {adminHistory.length > 0 && indivHistoryTotalPages > 0 && (
                              <div className="pagination">
                                <div className="page-info">
                                  Showing <b>{((indivHistoryPage - 1) * limit) + 1}</b> to <b>{Math.min(indivHistoryPage * limit, indivHistoryTotalRecords)}</b> of <b>{indivHistoryTotalRecords}</b> logs
                                </div>
                                <div className="page-btns">
                                  <button className="page-btn" disabled={indivHistoryPage === 1} onClick={() => setIndivHistoryPage(p => p - 1)} style={{ width: 'auto', padding: '0 12px' }}>Prev</button>
                                  {renderPagination(indivHistoryPage, indivHistoryTotalPages).map((p, i) => (
                                    <button
                                      key={i}
                                      className={`page-btn ${indivHistoryPage === p ? 'active' : ''}`}
                                      onClick={() => typeof p === 'number' && setIndivHistoryPage(p)}
                                      style={{
                                        cursor: typeof p === 'number' ? 'pointer' : 'default',
                                        border: typeof p === 'number' ? '' : 'none',
                                        background: typeof p === 'number' ? '' : 'transparent'
                                      }}
                                    >
                                      {p}
                                    </button>
                                  ))}
                                  <button className="page-btn" disabled={indivHistoryPage === indivHistoryTotalPages} onClick={() => setIndivHistoryPage(p => p + 1)} style={{ width: 'auto', padding: '0 12px' }}>Next</button>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </>
                  ) : (
                    <>
                      {/* ADMIN LIST */}
                      <div className="sec-title">
                        All Admin Accounts
                        {(typeof currentUser?.role === 'string' ? currentUser.role : currentUser?.role?.name)?.toLowerCase() === 'superadmin' && (
                          <button className="btn btn-primary" style={{ fontSize: 12, padding: '8px 14px' }} onClick={() => { setShowAddAdmin(true); setIsEditing(false); setFormData({ full_name: '', email: '', password: '', role: 'Super Admin', status: 1 }); }}>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
                            Add New Admin
                          </button>
                        )}
                      </div>

                      <div className="toolbar" style={{ padding: 0, border: 'none', marginBottom: 16 }}>
                        <div className="search-wrap">
                          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="var(--grey)" strokeWidth="2"><circle cx="11" cy="11" r="8" /><path d="M21 21l-4.35-4.35" /></svg>
                          <input
                            className="search-input"
                            placeholder="Search by name or email..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                          />
                        </div>
                        <select className="filter-select" value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)}>
                          <option value="">All Roles</option>
                          <option value="SuperAdmin">Super Admin</option>
                          <option value="Admin">Admin</option>
                        </select>
                        <select className="filter-select" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                          <option value="">All Statuses</option>
                          <option value="1">Active</option>
                          <option value="2">Inactive</option>
                        </select>
                      </div>

                      {/* TABLE */}
                      <div style={{ position: 'relative', minHeight: 200 }}>
                        {loading && <div style={{ position: 'absolute', inset: 0, background: 'rgba(255,255,255,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10 }}>Loading admins...</div>}
                        <table className="tbl" style={{ marginBottom: 0 }}>
                          <thead><tr><th>Admin</th><th>Role</th><th>Status</th><th>Last Login</th><th>Actions</th></tr></thead>
                          <tbody>
                            {admins.length === 0 && !loading && <tr><td colSpan="6" style={{ textAlign: 'center', padding: 40, color: 'var(--grey)' }}>No admin accounts found</td></tr>}
                            {admins.map((a, i) => (
                              <tr key={a.id || i}>
                                <td>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: 11 }}>
                                    <div style={{ width: 36, height: 36, borderRadius: '50%', background: 'linear-gradient(135deg,#EEF6FF,#DDEBFF)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, color: 'var(--blue)', flexShrink: 0 }}>{getInitials(a.full_name)}</div>
                                    <div>
                                      <div style={{ fontWeight: 600, fontSize: 13, color: a.status === 2 ? 'var(--grey)' : 'var(--navy)' }}>{a.full_name}</div>
                                      <div style={{ fontSize: 11, color: 'var(--grey)' }}>{a.email}</div>
                                    </div>
                                  </div>
                                </td>
                                <td><span className={`badge badge-${a.role?.name?.toLowerCase() === 'superadmin' ? 'super' : 'admin'}`}>{a.role?.name?.toLowerCase() === 'superadmin' ? '⭐ Super Admin' : 'Admin'}</span></td>
                                <td><span className={`badge badge-${a.status === 1 ? 'active' : 'inactive'}`}>{a.status === 1 ? 'Active' : 'Inactive'}</span></td>
                                <td style={{ fontSize: 12, color: 'var(--grey)' }}>{a.last_login_at ? new Date(a.last_login_at).toLocaleDateString() + ' · ' + new Date(a.last_login_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : 'Never'}</td>
                                <td>
                                  <div className="action-btns">
                                    <button className="action-btn" onClick={() => handleView(a.id)} title="View Details"><svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="var(--grey)" strokeWidth="1.8"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" /></svg></button>
                                    {canManage(a) && (
                                      <>
                                        <button className="action-btn" onClick={() => handleEdit(a)} title="Edit Admin"><svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="var(--grey)" strokeWidth="1.8"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" /></svg></button>
                                        <button className="action-btn warn" onClick={() => handleForcePasswordReset(a.id)} title="Force Password Reset"><svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#B8860B" strokeWidth="1.8"><rect x="3" y="11" width="18" height="11" rx="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" /></svg></button>
                                        <button
                                          className="action-btn danger"
                                          onClick={() => handleToggleStatus(a.id, a.status)}
                                          title={a.status === 1 ? 'Deactivate Admin' : 'Activate Admin'}
                                        >
                                          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={a.status === 1 ? 'var(--red)' : 'var(--green)'} strokeWidth="1.8">
                                            <circle cx="12" cy="12" r="10" /><line x1="4.93" y1="4.93" x2="19.07" y2="19.07" />
                                          </svg>
                                        </button>
                                        <button className="action-btn danger" onClick={() => confirmDelete(a)} title="Delete Admin">
                                          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="var(--red)" strokeWidth="1.8">
                                            <polyline points="3 6 5 6 21 6"></polyline>
                                            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                                            <line x1="10" y1="11" x2="10" y2="17"></line>
                                            <line x1="14" y1="11" x2="14" y2="17"></line>
                                          </svg>
                                        </button>
                                      </>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>

                        {admins.length > 0 && (
                          <div className="pagination">
                            <div className="page-info">
                              Showing <b>{((currentPage - 1) * limit) + 1}</b> to <b>{Math.min(currentPage * limit, totalRecords)}</b> of <b>{totalRecords}</b> admins
                            </div>
                            <div className="page-btns">
                              <button className="page-btn" disabled={currentPage === 1} onClick={() => setCurrentPage(p => p - 1)} style={{ width: 'auto', padding: '0 12px' }}>Prev</button>
                              {renderPagination(currentPage, totalPages).map((p, i) => (
                                <button
                                  key={i}
                                  className={`page-btn ${currentPage === p ? 'active' : ''}`}
                                  onClick={() => typeof p === 'number' && setCurrentPage(p)}
                                  style={{
                                    cursor: typeof p === 'number' ? 'pointer' : 'default',
                                    border: typeof p === 'number' ? '' : 'none',
                                    background: typeof p === 'number' ? '' : 'transparent'
                                  }}
                                >
                                  {p}
                                </button>
                              ))}
                              <button className="page-btn" disabled={currentPage === totalPages} onClick={() => setCurrentPage(p => p + 1)} style={{ width: 'auto', padding: '0 12px' }}>Next</button>
                            </div>
                          </div>
                        )}
                      </div>
                    </>
                  )}
                  <hr className="sec-divider" />
                </>
              )}

              {activeTab === 'Admin Accounts' && showAddAdmin && (
                <>
                  {/* ADD/EDIT ADMIN FORM */}
                  <div className="sec-title">{isEditing ? 'Update Admin Account' : 'Add New Admin Account'}</div>
                  <div className="form-grid">
                    <div className="form-group"><label className="form-label">Admin Name <span className="req">*</span></label><input className="form-input" name="full_name" value={formData.full_name} onChange={handleInputChange} placeholder="Full name of the admin user" /></div>
                    <div className="form-group"><label className="form-label">Email Address <span className="req">*</span></label><input className="form-input" type="email" name="email" value={formData.email} onChange={handleInputChange} placeholder="Login email for admin panel access" /></div>
                    <div className="form-group full"><label className="form-label">Password {isEditing ? '(Leave blank to keep current)' : <span className="req">*</span>}</label><input className="form-input" type="password" name="password" value={formData.password} onChange={handleInputChange} placeholder="Min 12 chars · Uppercase · Number · Symbol" /><div style={{ fontSize: 11, color: 'var(--grey)', marginTop: 4 }}>Minimum 12 characters. Must include at least one uppercase letter, one number, and one symbol.</div></div>
                    <div className="form-group full">
                      <label className="form-label">Role <span className="req">*</span></label>
                      <div className="role-grid">
                        <div className={`role-card ${formData.role === 'Admin' ? 'selected' : ''}`} onClick={() => handleRoleChange('Admin')}>
                          <input type="radio" name="role" checked={formData.role === 'Admin'} readOnly style={{ width: 15, height: 15, accentColor: 'var(--blue)', marginBottom: 8 }} />
                          <div className="role-name">Admin</div>
                          <div className="role-desc">Can manage airports, POIs, airlines, routes, gates, and users. Cannot manage other admin accounts.</div>
                        </div>
                        <div className={`role-card ${formData.role === 'Super Admin' ? 'selected' : ''}`} onClick={() => handleRoleChange('Super Admin')}>
                          <input type="radio" name="role" checked={formData.role === 'Super Admin'} readOnly style={{ width: 15, height: 15, accentColor: 'var(--blue)', marginBottom: 8 }} />
                          <div className="role-name">⭐ Super Admin</div>
                          <div className="role-desc">Full access to everything including admin account management. Highest level of access.</div>
                        </div>
                      </div>
                    </div>
                    <div className="form-group full">
                      <label className="form-label">Status <span className="req">*</span></label>
                      <div className="toggle-row">
                        <div><div style={{ fontSize: 13, fontWeight: 600 }}>Account Active</div><div style={{ fontSize: 11, color: 'var(--grey)', marginTop: 2 }}>Active = can log in to admin panel. Inactive = access revoked.</div></div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <span style={{ fontSize: 12, color: 'var(--grey)' }}>Inactive</span>
                          <button className={`toggle-switch ${formData.status === 1 ? 'on' : 'off'}`} onClick={handleStatusToggle} />
                          <span style={{ fontSize: 12, fontWeight: 700, color: formData.status === 1 ? 'var(--green)' : 'var(--grey)' }}>Active</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 20 }}>
                    <button className="btn btn-outline" onClick={() => { setShowAddAdmin(false); setIsEditing(false); }}>Cancel</button>
                    <button className="btn btn-primary" onClick={handleSubmit}><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" /><polyline points="17 21 17 13 7 13 7 21" /></svg>{isEditing ? 'Update Admin Account' : 'Create Admin Account'}</button>
                  </div>
                </>
              )}

              {activeTab === 'Login History' && (
                <>
                  {/* LOGIN HISTORY */}
                  <div className="sec-title">Global Admin Activity History</div>
                  <div style={{ position: 'relative', minHeight: 200 }}>
                    {loadingHistory && <div style={{ position: 'absolute', inset: 0, background: 'rgba(255,255,255,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10 }}>Loading history...</div>}
                    <table className="log-table" style={{ marginBottom: 0 }}>
                      <thead><tr><th>Admin</th><th>Action</th><th>Date & Time</th><th>IP Address</th><th>Result</th></tr></thead>
                      <tbody>
                        {globalLoginHistory.length === 0 && !loadingHistory && <tr><td colSpan="5" style={{ textAlign: 'center', padding: 40, color: 'var(--grey)' }}>No activity history found</td></tr>}
                        {globalLoginHistory.map((l, i) => (
                          <tr key={l.id || i}>
                            <td style={{ fontWeight: 600, color: 'var(--navy)' }}>{l.user?.full_name || 'System'}</td>
                            <td style={{ fontSize: 13, color: 'var(--dark)' }}>{l.action}</td>
                            <td style={{ fontSize: 12, color: 'var(--grey)' }}>{new Date(l.createdAt).toLocaleString()}</td>
                            <td style={{ fontSize: 12, color: 'var(--grey)', fontFamily: "'Space Grotesk', sans-serif" }}>{l.ip_address || '—'}</td>
                            <td><span className={l.result?.toLowerCase().includes('success') ? 'log-success' : 'log-fail'}>{l.result}</span></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>

                    {globalLoginHistory.length > 0 && (
                      <div className="pagination">
                        <div className="page-info">
                          Showing <b>{((historyPage - 1) * limit) + 1}</b> to <b>{Math.min(historyPage * limit, historyTotalRecords)}</b> of <b>{historyTotalRecords}</b> logs
                        </div>
                        <div className="page-btns">
                          <button className="page-btn" disabled={historyPage === 1} onClick={() => setHistoryPage(p => p - 1)} style={{ width: 'auto', padding: '0 12px' }}>Prev</button>
                          {renderPagination(historyPage, historyTotalPages).map((p, i) => (
                            <button
                              key={i}
                              className={`page-btn ${historyPage === p ? 'active' : ''}`}
                              onClick={() => typeof p === 'number' && setHistoryPage(p)}
                              style={{
                                cursor: typeof p === 'number' ? 'pointer' : 'default',
                                border: typeof p === 'number' ? '' : 'none',
                                background: typeof p === 'number' ? '' : 'transparent'
                              }}
                            >
                              {p}
                            </button>
                          ))}
                          <button className="page-btn" disabled={historyPage === historyTotalPages} onClick={() => setHistoryPage(p => p + 1)} style={{ width: 'auto', padding: '0 12px' }}>Next</button>
                        </div>
                      </div>
                    )}
                  </div>
                </>
              )}

              {activeTab === 'App Settings' && (
                <>
                  <div className="sec-title">App Settings</div>
                  <div className="card" style={{ padding: 24, border: '1px solid var(--border)' }}>
                    <div style={{ display: 'flex', gap: 20, marginBottom: 20 }}>
                      <div className="form-group" style={{ flex: 1 }}><label className="form-label">Map Link</label><input className="form-input" name="map_link" value={appSettings.map_link} onChange={handleAppSettingsChange} placeholder="e.g. https://www.google.com/maps" /></div>
                      <div className="form-group" style={{ flex: 1 }}><label className="form-label">App Version</label><input className="form-input" name="app_version" value={appSettings.app_version} onChange={handleAppSettingsChange} placeholder="e.g. 1.1.1" /></div>
                      <div className="form-group" style={{ flex: 1 }}><label className="form-label">Build Number</label><input className="form-input" name="build_number" value={appSettings.build_number} onChange={handleAppSettingsChange} placeholder="e.g. 42" /></div>
                    </div>

                    <div className="form-group full">
                        <label className="form-label" style={{ color: 'var(--grey)', textTransform: 'uppercase', fontSize: 12, fontWeight: 700, letterSpacing: 0.5, marginBottom: 8, display: 'block' }}>STATUS & FEATURES <span className="req" style={{ color: 'var(--red)' }}>*</span></label>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                          {/* Status Toggle */}
                          <div className="toggle-row" style={{ padding: '16px 20px', border: '1px solid #E2E8F0', borderRadius: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <div><div style={{ fontSize: 15, fontWeight: 500, color: 'var(--navy)' }}>Force Update</div><div style={{ fontSize: 13, color: 'var(--grey)', marginTop: 4 }}>Enabled = users forced to update when new version pushed to stores</div></div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                              <span style={{ fontSize: 12, color: 'var(--grey)' }}>Inactive</span>
                              <button className={`toggle-switch ${appSettings.force_update ? 'on' : 'off'}`} onClick={() => handleAppSettingsToggle('force_update')} />
                              <span style={{ fontSize: 12, fontWeight: 700, color: appSettings.force_update ? 'var(--green)' : 'var(--grey)' }}>Active</span>
                            </div>
                          </div>

                          {/* Enable Mock Location Toggle */}
                          <div className="toggle-row" style={{ padding: '16px 20px', border: '1px solid #E2E8F0', borderRadius: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <div><div style={{ fontSize: 15, fontWeight: 500, color: 'var(--navy)' }}>Enable mock location</div><div style={{ fontSize: 13, color: 'var(--grey)', marginTop: 4 }}>Enabled = test with mock locations</div></div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                              <span style={{ fontSize: 12, color: 'var(--grey)' }}>Disabled</span>
                              <button className={`toggle-switch ${appSettings.is_mock_location_enabled ? 'on' : 'off'}`} onClick={() => handleAppSettingsToggle('is_mock_location_enabled')} />
                              <span style={{ fontSize: 12, fontWeight: 700, color: appSettings.is_mock_location_enabled ? 'var(--green)' : 'var(--grey)' }}>Enabled</span>
                            </div>
                          </div>
                        </div>
                    </div>

                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 24 }}>
                      <button className="btn btn-primary" onClick={handleSaveAppSettings} disabled={savingSettings}>
                        {savingSettings ? 'Saving...' : 'Save Settings'}
                      </button>
                    </div>
                  </div>
                </>
              )}

            </div>
          </div>
        </div>
      </div>

      {/* DELETE CONFIRMATION MODAL */}
      {showDeleteModal && (
        <div className="modal-overlay">
          <div className="modal-card" style={{ maxWidth: 400 }}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', padding: '10px 0' }}>
              <div style={{ width: 60, height: 60, borderRadius: '50%', background: '#FFF0F0', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 20 }}>
                <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="var(--red)" strokeWidth="2"><circle cx="12" cy="12" r="10" /><line x1="15" y1="9" x2="9" y2="15" /><line x1="9" y1="9" x2="15" y2="15" /></svg>
              </div>
              <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--navy)', marginBottom: 10 }}>Delete Admin Account?</div>
              <div style={{ fontSize: 13, color: 'var(--grey)', lineHeight: 1.6, marginBottom: 24 }}>
                Are you sure you want to delete <span style={{ fontWeight: 700, color: 'var(--navy)' }}>{adminToDelete?.full_name}</span>? This action cannot be undone and all access will be permanently revoked.
              </div>
              <div style={{ display: 'flex', gap: 12, width: '100%' }}>
                <button className="btn btn-outline text-center" style={{ flex: 1, justifyContent: 'center' }} onClick={() => setShowDeleteModal(false)} disabled={isDeleting}>Cancel</button>
                <button className="btn btn-primary" style={{ flex: 1, background: 'var(--red)', border: 'none', justifyContent: 'center' }} onClick={handleDelete} disabled={isDeleting}>
                  {isDeleting ? 'Deleting...' : 'Delete Account'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <style>{`
      .modal-overlay {
        position: fixed;
        inset: 0;
        background: rgba(10, 31, 68, 0.4);
        backdrop-filter: blur(4px);
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 9999;
      }
      .modal-card {
        background: white;
        border-radius: 20px;
        padding: 32px;
        box-shadow: 0 20px 40px rgba(0,0,0,0.1);
        width: 90%;
        animation: modalIn 0.3s ease-out;
      }
      @keyframes modalIn {
        from { opacity: 0; transform: translateY(20px) scale(0.95); }
        to { opacity: 1; transform: translateY(0) scale(1); }
      }
      .action-row.interactive:hover {
        background: var(--bg-light);
        padding-left: 10px;
        border-radius: 8px;
      }
    `}</style>
    </div>
  );
};

export default Settings;
